<template>
  <div class="committee-voucher-page">
    <div class="figma-white-card">
      <!-- 页面标题 -->
      <div class="figma-page-title-bar">
        <div class="figma-page-title">{{ $t('committeeVoucherManage.pageTitle') }}</div>
      </div>

      <!-- 账户概览区域 -->
      <div class="account-overview">
        <div class="overview-header">
          <div>
            <div class="overview-label">{{ $t('committeeVoucherManage.overview.label') }}</div>
            <div class="overview-account-name">{{ accountInfo.accountName || '-' }}</div>
          </div>
          <el-select
            v-model="currentCommunityId"
            size="small"
            class="community-select"
            placeholder="请选择小区"
            @change="handleCommunityChange"
          >
            <el-option
              v-for="c in communityList"
              :key="c.communityId"
              :label="c.name"
              :value="c.communityId"
            />
          </el-select>
        </div>
        <div class="overview-card">
          <div class="overview-left">
            <div class="overview-card-title">{{ $t('committeeVoucherManage.overview.fundAccount') }}</div>
            <div class="overview-card-number">{{ accountInfo.accountNo || '-' }}</div>
          </div>
          <div class="overview-right">
            <div class="overview-card-title">{{ $t('committeeVoucherManage.overview.balance') }}</div>
            <div class="overview-card-amount">¥ {{ accountInfo.balance || '0.00' }}</div>
          </div>
        </div>
      </div>

      <!-- 工具栏 -->
      <div class="figma-toolbar-row">
        <div class="figma-btn-group">
          <el-button size="small" class="figma-btn figma-btn-primary" @click="handleAdd">
            <i class="el-icon-plus"></i>{{ $t('committeeVoucherManage.action.add') }}
          </el-button>
          <el-button size="small" class="figma-btn-refresh" :title="$t('committeeVoucherManage.action.refresh')" @click="getList">
            <i class="el-icon-refresh-right"></i>
          </el-button>
        </div>
        <div class="figma-search-group">
          <el-input
            v-model="conditions.keyword"
            size="small"
            :placeholder="$t('committeeVoucherManage.search.keywordPlaceholder')"
            class="figma-search-input"
            clearable
            @keyup.enter.native="handleSearch"
          />
          <el-button size="small" class="figma-btn-search" @click="handleSearch">
            <i class="el-icon-search"></i>
            <span>{{ $t('committeeVoucherManage.action.search') }}</span>
          </el-button>
        </div>
      </div>

      <div class="figma-table-section">
        <el-table
          v-loading="loading"
          :data="tableData"
          style="width: 100%"
          :header-cell-style="headerCellStyle"
          :cell-style="cellStyle"
          @selection-change="handleSelectionChange"
        >
          <el-table-column type="selection" width="40" align="center" />
          <el-table-column :label="$t('committeeVoucherManage.table.id')" width="112">
            <template slot-scope="scope">{{ scope.row.idText }}</template>
          </el-table-column>
          <el-table-column prop="voucherNo" :label="$t('committeeVoucherManage.table.voucherNo')" width="139" show-overflow-tooltip />
          <el-table-column prop="feeItem" :label="$t('committeeVoucherManage.table.feeItem')" width="105" show-overflow-tooltip />
          <el-table-column :label="$t('committeeVoucherManage.table.feeType')" width="136">
            <template slot-scope="scope">
              <span :class="['fee-type-tag', scope.row.feeType === 'INCOME' ? 'fee-type-income' : 'fee-type-expense']">
                {{ scope.row.feeTypeName }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="payMethodName" :label="$t('committeeVoucherManage.table.payMethod')" width="200" show-overflow-tooltip />
          <el-table-column :label="$t('committeeVoucherManage.table.amount')" width="248">
            <template slot-scope="scope">
              <span :style="{ color: scope.row.feeType === 'INCOME' ? '#0E76FF' : '#1D2129' }">{{ scope.row.amountText }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="startTime" :label="$t('committeeVoucherManage.table.startTime')" width="351" show-overflow-tooltip />
          <el-table-column :label="$t('committeeVoucherManage.table.action')" width="255">
            <template slot-scope="scope">
              <div class="action-links">
                <a class="action-link" @click="handleDetail(scope.row)">{{ $t('committeeVoucherManage.action.detail') }}</a>
                <span class="link-space"></span>
                <a class="action-link" @click="handleEdit(scope.row)">{{ $t('committeeVoucherManage.action.edit') }}</a>
                <span class="link-space"></span>
                <a class="action-link" @click="handleDelete(scope.row)">{{ $t('committeeVoucherManage.action.delete') }}</a>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="figma-pagination">
        <span class="pagination-total">共{{ pagination.total }}条</span>
        <el-pagination
          :current-page="pagination.page"
          :page-size="pagination.row"
          layout="prev, pager, next"
          :total="pagination.total"
          @current-change="handlePageChange"
        />
        <el-select v-model="pagination.row" size="small" class="pagination-size-select" @change="handleSizeChange">
          <el-option label="10条/页" :value="10" />
          <el-option label="20条/页" :value="20" />
          <el-option label="50条/页" :value="50" />
        </el-select>
      </div>
    </div>

    <!-- 添加/编辑凭证弹窗 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="650px"
      custom-class="voucher-dialog"
      :close-on-click-modal="false"
    >
      <el-form
        ref="form"
        :model="form"
        :rules="formRules"
        label-width="86px"
        label-position="right"
        class="voucher-form-body"
      >
        <el-form-item :label="$t('committeeVoucherManage.form.voucherNo') + '：'" prop="voucherNo" required>
          <el-input v-model="form.voucherNo" :placeholder="$t('committeeVoucherManage.form.voucherNoPlaceholder')" :disabled="dialogMode === 'edit'" />
        </el-form-item>

        <el-form-item :label="$t('committeeVoucherManage.form.feeType') + '：'" prop="feeType" required>
          <el-select v-model="form.feeType" style="width: 100%" :placeholder="$t('committeeVoucherManage.form.feeTypePlaceholder')" :disabled="dialogMode === 'edit'">
            <el-option :label="$t('committeeVoucherManage.form.expense')" value="EXPENSE" />
            <el-option :label="$t('committeeVoucherManage.form.income')" value="INCOME" />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('committeeVoucherManage.form.feeItem') + '：'" prop="feeItem" required>
          <el-input v-model="form.feeItem" :placeholder="$t('committeeVoucherManage.form.feeItemPlaceholder')" />
        </el-form-item>

        <el-form-item :label="$t('committeeVoucherManage.form.payMethod') + '：'" prop="transferMethod" required>
          <el-select v-model="form.transferMethod" style="width: 100%" :placeholder="$t('committeeVoucherManage.form.payMethodPlaceholder')">
            <el-option :label="$t('committeeVoucherManage.form.bankTransfer')" value="BANK_TRANSFER" />
            <el-option :label="$t('committeeVoucherManage.form.cash')" value="CASH" />
            <el-option :label="$t('committeeVoucherManage.form.wechat')" value="WECHAT" />
            <el-option :label="$t('committeeVoucherManage.form.alipay')" value="ALIPAY" />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('committeeVoucherManage.form.amount') + '：'" prop="amount" required>
          <el-input v-model="form.amount" :placeholder="$t('committeeVoucherManage.form.amountPlaceholder')" type="number" />
        </el-form-item>

        <el-form-item :label="$t('committeeVoucherManage.form.time') + '：'" prop="time" required>
          <el-date-picker
            v-model="form.time"
            type="datetime"
            style="width: 100%"
            :placeholder="$t('committeeVoucherManage.form.timePlaceholder')"
            value-format="yyyy/MM/dd HH:mm"
          />
        </el-form-item>

        <el-form-item :label="$t('committeeVoucherManage.form.attachment') + '：'" prop="attachment">
          <el-upload
            v-if="dialogMode === 'add'"
            action="#"
            :auto-upload="false"
            :show-file-list="false"
            :on-change="handleFileChange"
          >
            <div class="upload-box">
              <i class="el-icon-plus upload-icon"></i>
            </div>
          </el-upload>
          <div v-if="form.fileName" class="uploaded-file">
            <a class="file-link" @click="previewAttachment" target="_blank">{{ form.fileName }}</a>
            <i v-if="dialogMode === 'add'" class="el-icon-close" @click="removeFile"></i>
          </div>
        </el-form-item>
      </el-form>

      <span slot="footer" class="voucher-footer">
        <el-button class="voucher-btn-cancel" @click="dialogVisible = false">{{ $t('committeeVoucherManage.dialog.cancel') }}</el-button>
        <el-button class="voucher-btn-save" :loading="submitting" @click="submitForm">{{ $t('committeeVoucherManage.dialog.confirm') }}</el-button>
      </span>
    </el-dialog>

    <!-- 详情弹窗 -->
    <el-dialog
      :title="$t('committeeVoucherManage.dialog.detailTitle')"
      :visible.sync="detailVisible"
      width="650px"
      custom-class="voucher-dialog voucher-detail-dialog"
      :close-on-click-modal="false"
    >
      <div class="detail-content">
        <div class="detail-row">
          <div class="detail-label">{{ $t('committeeVoucherManage.form.voucherNo') }}</div>
          <div class="detail-value">{{ detailData.voucherNo || '-' }}</div>
        </div>
        <div class="detail-row">
          <div class="detail-label">{{ $t('committeeVoucherManage.form.feeType') }}</div>
          <div class="detail-value">
            <span :class="['fee-type-tag', detailData.feeType === 'INCOME' ? 'fee-type-income' : 'fee-type-expense']">
              {{ detailData.feeTypeName }}
            </span>
          </div>
        </div>
        <div class="detail-row">
          <div class="detail-label">{{ $t('committeeVoucherManage.form.feeItem') }}</div>
          <div class="detail-value">{{ detailData.feeItem || '-' }}</div>
        </div>
        <div class="detail-row">
          <div class="detail-label">{{ $t('committeeVoucherManage.form.payMethod') }}</div>
          <div class="detail-value">{{ detailData.payMethodName || '-' }}</div>
        </div>
        <div class="detail-row">
          <div class="detail-label">{{ $t('committeeVoucherManage.form.amount') }}</div>
          <div class="detail-value">{{ detailData.amountText || '-' }}</div>
        </div>
        <div class="detail-row">
          <div class="detail-label">{{ $t('committeeVoucherManage.form.time') }}</div>
          <div class="detail-value">{{ detailData.startTime || '-' }}</div>
        </div>
        <div class="detail-row">
          <div class="detail-label">{{ $t('committeeVoucherManage.form.attachment') }}</div>
          <div class="detail-value">{{ detailData.fileName || getAttachmentName(detailData) || '-' }}</div>
        </div>
      </div>
      <span slot="footer" class="voucher-footer">
        <el-button class="voucher-btn-cancel" @click="detailVisible = false">{{ $t('committeeVoucherManage.dialog.close') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import { listCommunitysByOwnerCommittee } from '@/api/owner/ownerCommitteeManageApi'
import {
  listVouchers,
  getVoucherDetail,
  saveVoucher,
  updateVoucher,
  deleteVoucher,
  uploadVoucherAttachment,
  getCommunityPublicAccount
} from '@/api/committee/voucherManageApi'

export default {
  name: 'CommitteeVoucherManageList',
  data() {
    return {
      loading: false,
      submitting: false,
      dialogVisible: false,
      detailVisible: false,
      dialogMode: 'add',
      conditions: { keyword: '' },
      tableData: [],
      multipleSelection: [],
      pagination: { total: 0, page: 1, row: 10 },
      accountInfo: {
        accountName: '',
        accountNo: '',
        bankName: '',
        balance: ''
      },
      communityList: [],
      currentCommunityId: '',
      form: this.getDefaultForm(),
      detailData: {},
      formRules: {
        voucherNo: [
          { required: true, message: this.$t('committeeVoucherManage.validate.voucherNo'), trigger: 'blur' }
        ],
        feeType: [
          { required: true, message: this.$t('committeeVoucherManage.validate.feeType'), trigger: 'change' }
        ],
        feeItem: [
          { required: true, message: this.$t('committeeVoucherManage.validate.feeItem'), trigger: 'blur' }
        ],
        transferMethod: [
          { required: true, message: this.$t('committeeVoucherManage.validate.payMethod'), trigger: 'change' }
        ],
        amount: [
          { required: true, message: this.$t('committeeVoucherManage.validate.amount'), trigger: 'blur' }
        ],
        time: [
          { required: true, message: this.$t('committeeVoucherManage.validate.time'), trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.dialogMode === 'add'
        ? this.$t('committeeVoucherManage.dialog.addTitle')
        : this.$t('committeeVoucherManage.dialog.editTitle')
    }
  },
  created() {
    this.initPage()
  },
  methods: {
    getDefaultForm() {
      return { voucherId: '', communityId: '', voucherNo: '', feeType: '', feeItem: '', transferMethod: '', amount: '', time: '', attachment: '', fileName: '' }
    },
    headerCellStyle() {
      return { background: '#EBF2FA', color: '#364153', fontSize: '14px', fontWeight: '400', fontFamily: 'PingFang SC', height: '50px', padding: '0', textAlign: 'left' }
    },
    cellStyle({ row }) {
      const rowIndex = this.tableData.indexOf(row)
      const base = { color: '#1D2129', fontSize: '14px', fontFamily: 'PingFang SC', height: '50px', padding: '0', fontWeight: rowIndex % 2 === 1 ? '500' : '400', textAlign: 'left' }
      return rowIndex % 2 === 1 ? { ...base, background: '#F8FBFF' } : { ...base, background: 'white' }
    },
    isSuccess(res) {
      return res && (res.code === 0 || res.code === 200 || res.code === '0' || res.code === '200' || res.success || res.msg === 'success')
    },
    getListArray(res) {
      if (Array.isArray(res)) return res
      if (Array.isArray(res && res.data)) return res.data
      if (Array.isArray(res && res.records)) return res.records
      if (Array.isArray(res && res.rows)) return res.rows
      return []
    },
    getTotal(res, list) {
      return Number((res && (res.total || res.count || res.totalCount)) || list.length || 0)
    },
    getFeeTypeName(feeType) {
      if (feeType === 'INCOME') return this.$t('committeeVoucherManage.form.income')
      if (feeType === 'EXPENSE') return this.$t('committeeVoucherManage.form.expense')
      return feeType || '-'
    },
    getPayMethodName(payMethod) {
      const map = {
        'BANK_TRANSFER': this.$t('committeeVoucherManage.form.bankTransfer'),
        'CASH': this.$t('committeeVoucherManage.form.cash'),
        'WECHAT': this.$t('committeeVoucherManage.form.wechat'),
        'ALIPAY': this.$t('committeeVoucherManage.form.alipay')
      }
      return map[payMethod] || payMethod || '-'
    },
    getAttachmentName(row) {
      if (row.fileName) return row.fileName
      const attachmentUrl = row.attachmentUrl || row.attachment || ''
      if (!attachmentUrl) return ''
      return attachmentUrl.split('/').pop() || attachmentUrl
    },
    normalizeRow(row, index) {
      const attachmentUrl = row.attachmentUrl || row.attachment || ''
      return {
        ...row,
        idText: row.id || row.voucherId || (index + 1),
        voucherNo: row.voucherNo || '',
        feeItem: row.feeItem || '',
        feeType: row.feeType || '',
        feeTypeName: this.getFeeTypeName(row.feeType),
        payMethodName: this.getPayMethodName(row.transferMethod || row.payMethod),
        amountText: row.amount ? (Number(row.amount).toFixed(2) + ' 元') : '-',
        startTime: row.startTime || row.time || row.createTime || '',
        attachmentUrl,
        fileName: this.getAttachmentName({ ...row, attachmentUrl })
      }
    },
    async initPage() {
      await this.loadAccountInfo()
      this.getList()
    },
    async loadAccountInfo() {
      try {
        const communityRes = await listCommunitysByOwnerCommittee({ page: 1, row: 1000 })
        const communities = (communityRes && communityRes.data) || []
        this.communityList = communities
        let communityId = this.currentCommunityId
        if (communities.length > 0 && !communityId) {
          communityId = communities[0].communityId
          this.currentCommunityId = communityId
        }
        if (!communityId) {
          this.accountInfo = { accountName: '', accountNo: '', bankName: '', balance: '' }
          return
        }
        const currentCommunity = communities.find(c => c.communityId === this.currentCommunityId) || communities[0] || {}
        const res = await getCommunityPublicAccount({ communityId, page: 1, row: 100 })
        const account = (res && (res.data || res.records || res)) || {}
        const accountName = account.accountName || account.acctName || account.name || ''
        const accountNo = account.accountNo || account.acctNo || account.bankAccount || account.bankAccountNo || ''
        const bankName = account.bankName || account.bank || account.bankAlias || ''
        const balance = account.balance || account.accountBalance || account.amount || ''
        this.accountInfo = {
          accountName: accountName || (currentCommunity.name ? currentCommunity.name + this.$t('committeeVoucherManage.overview.suffix') : ''),
          accountNo: accountNo || currentCommunity.accountNo || '',
          bankName: bankName || currentCommunity.bankName || '',
          balance: balance || currentCommunity.balance || ''
        }
      } catch (e) {
        this.accountInfo = { accountName: '', accountNo: '', bankName: '', balance: '' }
      }
    },
    handleCommunityChange() {
      this.loadAccountInfo()
      this.pagination.page = 1
      this.getList()
    },
    async getList() {
      try {
        this.loading = true
        const res = await listVouchers({
          page: this.pagination.page,
          row: this.pagination.row,
          keyword: this.conditions.keyword,
          communityId: this.currentCommunityId
        })
        const list = this.getListArray(res)
        this.tableData = list.map((item, index) => this.normalizeRow(item, index))
        this.pagination.total = this.getTotal(res, list)
      } catch (e) {
        this.$message.error(this.$t('committeeVoucherManage.message.fetchError'))
      } finally {
        this.loading = false
      }
    },
    handleSearch() { this.pagination.page = 1; this.getList() },
    handleSelectionChange(val) { this.multipleSelection = val },
    handleAdd() {
      this.dialogMode = 'add'
      this.form = this.getDefaultForm()
      this.form.communityId = this.currentCommunityId
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate()
      })
    },
    handleEdit(row) {
      this.dialogMode = 'edit'
      this.form = {
        voucherId: row.voucherId || row.id,
        communityId: this.currentCommunityId,
        voucherNo: row.voucherNo || '',
        feeType: row.feeType || '',
        feeItem: row.feeItem || '',
        transferMethod: row.transferMethod || row.payMethod || '',
        amount: row.amount || '',
        time: row.startTime || row.time || '',
        attachment: row.attachmentUrl || row.attachment || '',
        fileName: row.fileName || this.getAttachmentName(row)
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate()
      })
    },
    handleDetail(row) {
      const voucherId = row.voucherId || row.id
      if (!voucherId) {
        this.$message.error('缺少凭证ID')
        return
      }
      this.$router.push({
        path: '/pages/committee/voucherDetail',
        query: { voucherId, communityId: this.currentCommunityId }
      })
    },
    handleDelete(row) {
      this.$confirm(this.$t('committeeVoucherManage.dialog.deleteConfirm'), this.$t('committeeVoucherManage.dialog.deleteTitle'), { type: 'warning' }).then(async () => {
        const res = await deleteVoucher({ voucherId: row.voucherId || row.id })
        if (!this.isSuccess(res)) throw new Error((res && res.msg) || this.$t('committeeVoucherManage.message.deleteError'))
        this.$message.success(this.$t('committeeVoucherManage.dialog.deleteSuccess'))
        this.loadAccountInfo()
        this.getList()
      }).catch(e => {
        if (e && e.message) this.$message.error(e.message)
      })
    },
    async handleFileChange(file) {
      try {
        const formData = new FormData()
        formData.append('uploadFile', file.raw)
        formData.append('communityId', this.form.communityId || this.currentCommunityId || getCommunityId())
        const res = await uploadVoucherAttachment(formData)
        const attachmentUrl = this.normalizeUploadUrl(res)
        if (!attachmentUrl) {
          throw new Error('附件上传未返回地址')
        }
        this.form.fileName = file.name
        this.form.attachment = attachmentUrl
      } catch (e) {
        this.form.fileName = ''
        this.form.attachment = ''
        this.$message.error(e.message || '附件上传失败')
      }
    },
    normalizeUploadUrl(res) {
      if (!res) return ''
      if (typeof res === 'string') return res
      const data = res.data || res
      return data.url || data.imageUrl || data.fileUrl || data.path || data.filePath || ''
    },
    removeFile() {
      this.form.fileName = ''
      this.form.attachment = ''
    },
    previewAttachment() {
      if (this.form.attachment) {
        window.open(this.form.attachment, '_blank')
      }
    },
    buildVoucherPayload() {
      const payload = { ...this.form }
      payload.attachmentUrl = payload.attachment || ''
      delete payload.attachment
      return payload
    },
    async submitForm() {
      const valid = await this.$refs.form.validate().catch(() => false)
      if (!valid) return
      try {
        this.submitting = true
        const api = this.dialogMode === 'add' ? saveVoucher : updateVoucher
        const res = await api(this.buildVoucherPayload())
        if (!this.isSuccess(res)) throw new Error((res && res.msg) || this.$t('committeeVoucherManage.message.saveError'))
        this.$message.success(this.$t('committeeVoucherManage.message.saveSuccess'))
        this.dialogVisible = false
        this.loadAccountInfo()
        this.getList()
      } catch (e) {
        this.$message.error(e.message || this.$t('committeeVoucherManage.message.saveError'))
      } finally {
        this.submitting = false
      }
    },
    handlePageChange(page) { this.pagination.page = page; this.getList() },
    handleSizeChange(size) { this.pagination.row = size; this.pagination.page = 1; this.getList() }
  }
}
</script>

<style scoped>
.committee-voucher-page {
  height: calc(100vh - 60px);
  background: #EBF2FA;
  padding: 0;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.figma-white-card {
  background: white;
  border-radius: 0 0 12px 12px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* 账户概览 */
.account-overview {
  margin: 20px 24px 0;
  padding: 20px 24px;
  background: #F3F7FB;
  border-radius: 8px;
  flex-shrink: 0;
}

.overview-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
}

.community-select {
  width: 180px;
}

.community-select >>> .el-input__inner {
  height: 38px;
  border-radius: 10px;
  border: 1px solid #DCE6F5;
  font-size: 14px;
  color: #1D2129;
  background: white;
}

.overview-label {
  font-size: 14px;
  font-weight: 600;
  color: #1D2129;
  margin-bottom: 8px;
  font-family: 'PingFang SC';
}

.overview-account-name {
  font-size: 16px;
  font-weight: 600;
  color: #1D2129;
  margin-bottom: 12px;
  font-family: 'PingFang SC';
}

.overview-card {
  background: white;
  border-radius: 8px;
  padding: 20px 24px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.overview-left {
  flex: 1;
}

.overview-right {
  flex: 1;
}

.overview-card-title {
  font-size: 14px;
  font-weight: 600;
  color: #8C9BB4;
  margin-bottom: 8px;
  font-family: 'PingFang SC';
}

.overview-card-number {
  font-size: 20px;
  font-weight: 600;
  color: #1D2129;
  margin-bottom: 4px;
  font-family: 'PingFang SC';
  word-break: break-all;
}

.overview-card-bank {
  font-size: 14px;
  font-weight: 600;
  color: #8C9BB4;
  font-family: 'PingFang SC';
}

.overview-card-amount {
  font-size: 22px;
  font-weight: 700;
  color: #0E76FF;
  font-family: 'D-DIN', 'PingFang SC';
}

/* 页面标题 */
.figma-page-title-bar {
  padding: 20px 24px 0;
  flex-shrink: 0;
}

.figma-page-title {
  font-size: 16px;
  font-weight: 600;
  color: #1D2129;
  font-family: 'PingFang SC';
  line-height: 22px;
}

/* 工具栏 */
.figma-toolbar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 12px;
  flex-shrink: 0;
}

.figma-btn-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.figma-btn {
  height: 38px;
  min-width: 82px;
  border-radius: 10px !important;
  border: none !important;
  color: white !important;
  font-size: 14px;
  font-weight: 500;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.figma-btn i {
  margin-right: 6px;
}

.figma-btn-primary {
  background: #0E76FF !important;
}

.figma-btn-refresh {
  height: 38px;
  width: 38px;
  border-radius: 10px !important;
  border: 1px solid #DCE6F5 !important;
  background: white !important;
  color: #6A7282 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.figma-btn-refresh:hover {
  border-color: #0E76FF !important;
  color: #0E76FF !important;
}

.figma-search-group {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.figma-search-input {
  width: 256px;
  height: 38px;
}

.figma-search-input >>> .el-input__inner {
  border-radius: 10px;
  border: none;
  outline: 1px #DCE6F5 solid;
  outline-offset: -1px;
  height: 38px;
  font-size: 14px;
  background: white;
  color: #1D2129;
}

.figma-search-input >>> .el-input__inner:focus {
  outline-color: #0E76FF;
}

.figma-btn-search {
  width: 84px;
  height: 38px;
  border-radius: 10px !important;
  border: none !important;
  outline: 1px #0E76FF solid !important;
  outline-offset: -1px;
  color: #0E76FF !important;
  background: white !important;
  font-size: 14px;
  font-weight: 500;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.figma-btn-search:hover {
  background: rgba(14, 118, 255, 0.08) !important;
}

/* 表格 */
.figma-table-section {
  flex: 1;
  min-height: 0;
  padding: 12px 24px 0;
  overflow: auto;
}

.figma-table-section >>> .el-table {
  border-radius: 8px;
  overflow: hidden;
  border: none;
}

.figma-table-section >>> .el-table::before {
  display: none;
}

.figma-table-section >>> .el-table__header th {
  background: #EBF2FA !important;
  border: none !important;
}

.figma-table-section >>> .el-table__body td {
  border: none !important;
}

.figma-table-section >>> .el-table th .cell,
.figma-table-section >>> .el-table td .cell {
  padding-left: 24px;
  padding-right: 24px;
  line-height: 22px;
  text-align: left;
}

/* 费用类型标签 */
.fee-type-tag {
  display: inline-block;
  padding: 2px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'PingFang SC';
  font-weight: 400;
  line-height: 24px;
}

.fee-type-expense {
  background: rgba(255, 105, 0, 0.10);
  border: 1px solid #FF6900;
  color: #FF6900;
}

.fee-type-income {
  background: rgba(14, 118, 255, 0.10);
  border: 1px solid #0E76FF;
  color: #0E76FF;
}

/* 操作链接 */
.action-links {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  white-space: nowrap;
}

.action-link {
  color: #0E76FF;
  font-size: 14px;
  cursor: pointer;
  text-decoration: none;
  font-weight: 400;
}

.action-link:hover {
  opacity: 0.85;
}

.link-space {
  width: 12px;
  display: inline-block;
}

/* 分页 */
.figma-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px 24px 24px;
  gap: 8px;
  flex-shrink: 0;
}

.pagination-total {
  font-size: 14px;
  color: #1D2129;
  margin-right: 4px;
}

.figma-pagination >>> .el-pagination {
  display: flex;
  align-items: center;
  padding: 0;
}

.figma-pagination >>> .btn-prev,
.figma-pagination >>> .btn-next {
  width: 32px;
  height: 32px;
  border-radius: 2px;
  background: white;
}

.figma-pagination >>> .el-pager li {
  min-width: 32px;
  height: 32px;
  line-height: 32px;
  border-radius: 8px;
  color: #1D2129;
  font-weight: 600;
}

.figma-pagination >>> .el-pager li.active {
  background: #E8F3FF;
  color: #0E76FF;
}

.pagination-size-select {
  width: 92px;
  margin-left: 8px;
}

.pagination-size-select >>> .el-input__inner {
  height: 32px;
  border-radius: 8px;
  background: #F2F3F5;
  border: none;
  font-size: 14px;
  color: #1D2129;
}

/* 弹窗样式 */
.committee-voucher-page >>> .voucher-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.committee-voucher-page >>> .voucher-dialog .el-dialog__header {
  height: 66px;
  box-sizing: border-box;
  padding: 22px 24px;
  background: #EBF2FA;
  border-bottom: 1px solid #DCE6F5;
}

.committee-voucher-page >>> .voucher-dialog .el-dialog__title {
  font-size: 16px;
  color: #1D2129;
  font-weight: 600;
}

.committee-voucher-page >>> .voucher-dialog .el-dialog__body {
  padding: 24px 24px 8px;
}

.committee-voucher-page >>> .voucher-dialog .el-dialog__footer {
  padding: 8px 24px 28px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.voucher-form-body .el-form-item {
  margin-bottom: 20px;
}

.voucher-form-body .el-form-item:last-child {
  margin-bottom: 0;
}

.voucher-form-body >>> .el-form-item__label {
  font-size: 14px;
  font-weight: 500;
  color: #1D2129;
}

.voucher-form-body >>> .el-form-item.is-required .el-form-item__label::before {
  color: #FB2C36;
  margin-right: 4px;
}

.voucher-form-body >>> .el-input__inner {
  border-radius: 10px;
  border: 1px solid #DCE6F5;
  font-size: 14px;
  color: #1D2129;
  height: 38px;
}

.voucher-form-body >>> .el-input__inner:focus {
  border-color: #0E76FF;
}

.voucher-form-body >>> .el-select .el-input__inner {
  height: 38px;
}

.voucher-form-body >>> .el-date-editor .el-input__inner {
  height: 38px;
}

/* 上传区域 */
.upload-box {
  width: 82px;
  height: 82px;
  background: rgba(14, 118, 255, 0.08);
  border-radius: 8px;
  border: 1px solid #0E76FF;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.upload-icon {
  font-size: 24px;
  color: #0E76FF;
}

.uploaded-file {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #1D2129;
}

.uploaded-file i {
  cursor: pointer;
  color: #8C9BB4;
  font-size: 14px;
}

.uploaded-file i:hover {
  color: #F56C6C;
}

.file-link {
  color: #0E76FF;
  cursor: pointer;
  text-decoration: underline;
}

.file-link:hover {
  opacity: 0.85;
}

/* 弹窗底部按钮 */
.voucher-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.voucher-btn-cancel {
  width: 98px;
  height: 38px;
  border-radius: 10px !important;
  color: #0E76FF !important;
  border-color: #0E76FF !important;
  background: white !important;
}

.voucher-btn-save {
  width: 98px;
  height: 38px;
  border-radius: 10px !important;
  background: #0E76FF !important;
  color: white !important;
  border-color: #0E76FF !important;
}

/* 详情弹窗 */
.detail-content {
  padding: 0;
}

.detail-row {
  display: flex;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid #F2F3F5;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  width: 100px;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 500;
  color: #8C9BB4;
  font-family: 'PingFang SC';
}

.detail-value {
  flex: 1;
  font-size: 14px;
  color: #1D2129;
  font-family: 'PingFang SC';
  word-break: break-all;
}

@media (max-width: 1024px) {
  .figma-header-row {
    align-items: flex-start;
    gap: 12px;
    flex-direction: column;
  }
  .figma-btn-group { flex-wrap: wrap; }
  .figma-search-group { width: 100%; margin-left: 0; }
  .figma-search-input { width: min(256px, calc(100vw - 150px)); }
  .overview-card { flex-direction: column; gap: 16px; }
}
</style>
